if (!customElements.get('gn-cart-drawer')) {
  customElements.define('gn-cart-drawer', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.dialog = this.querySelector('dialog');
      this.strings = JSON.parse(this.querySelector('[data-cart-strings]').textContent);
      this.items = this.querySelector('[data-cart-items]');
      this.checkout = this.querySelector('[data-cart-checkout]');
      this.status = this.querySelector('[data-cart-status]');
      this.error = this.querySelector('[data-cart-error]');
      document.addEventListener('click', event => {
        const link = event.target.closest('.gn-cart-link, [data-cart-open]');
        if (!link || event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button > 0) return;
        event.preventDefault();
        if (!this.cart) this.showSkeleton();
        this.open(link);
        if (!this.busy) this.refresh();
      }, options);
      this.querySelectorAll('[data-cart-close]').forEach(button => button.addEventListener('click', () => this.dialog.close(), options));
      this.dialog.addEventListener('click', event => {
        if (event.target !== this.dialog) return;
        const rect = this.dialog.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.dialog.close();
      }, options);
      this.dialog.addEventListener('close', () => {
        document.documentElement.classList.remove('gn-cart-open');
        this.setExpanded(false);
        if (this.opener?.isConnected) this.opener.focus({ preventScroll: true });
      }, options);
      this.querySelector('[data-cart-retry]').addEventListener('click', () => this.refresh(), options);
      this.checkout.addEventListener('submit', event => { if (this.busy || !this.cart?.item_count || this.stale) event.preventDefault(); }, options);
      this.items.addEventListener('click', event => {
        const button = event.target.closest('[data-cart-step], [data-cart-remove]');
        if (!button || this.busy) return;
        const row = button.closest('[data-line-key]');
        const input = row.querySelector('[data-cart-quantity]');
        const current = Number(input.value);
        const next = button.hasAttribute('data-cart-remove') ? 0 : current + Number(button.dataset.cartStep) * Number(input.step);
        const minimum = Number(input.min);
        this.change(row.dataset.lineKey, next < minimum ? 0 : next, button.hasAttribute('data-cart-remove') ? 'remove' : button.dataset.cartStep);
      }, options);
      this.items.addEventListener('change', event => {
        const input = event.target.closest('[data-cart-quantity]');
        if (!input || this.busy) return;
        if (input.value.trim() === '') {
          const key = input.closest('[data-line-key]').dataset.lineKey;
          input.value = this.cart.items.find(item => item.key === key)?.quantity ?? 1;
          return;
        }
        const quantity = Number(input.value);
        if (!Number.isInteger(quantity) || quantity < 0 || (quantity !== 0 && !input.checkValidity())) { input.reportValidity(); this.render(this.cart); return; }
        this.change(input.closest('[data-line-key]').dataset.lineKey, quantity, 'input');
      }, options);
      window.addEventListener('pageshow', () => { if (this.dialog.open && !this.busy) this.refresh(); }, options);
      this.setExpanded(false);
    }

    disconnectedCallback() {
      this.controller?.abort();
      if (this.dialog?.open) this.dialog.close();
      document.documentElement.classList.remove('gn-cart-open');
      this.initialized = false;
    }

    setExpanded(open) {
      document.querySelectorAll('.gn-cart-link, [data-cart-open]').forEach(link => {
        link.setAttribute('aria-haspopup', 'dialog');
        link.setAttribute('aria-controls', this.dialog.id);
        link.setAttribute('aria-expanded', String(open));
      });
    }

    open(opener) {
      if (this.dialog.open) return;
      this.opener = opener || document.activeElement;
      document.querySelectorAll('gn-header-menu').forEach(menu => menu.close?.(false));
      this.dialog.showModal();
      document.documentElement.classList.add('gn-cart-open');
      this.setExpanded(true);
    }

    setBusy(busy, message = '') {
      this.busy = busy;
      this.items.setAttribute('aria-busy', String(busy));
      this.items.querySelectorAll('button, input').forEach(control => { control.disabled = busy; });
      if (!busy) this.items.querySelectorAll('[data-line-key]').forEach(row => {
        const input = row.querySelector('[data-cart-quantity]');
        if (input.max && Number(input.value) + Number(input.step) > Number(input.max)) row.querySelector('[data-cart-step="1"]').disabled = true;
      });
      this.querySelector('[data-cart-checkout-button]').disabled = busy || this.stale;
      this.querySelector('[data-cart-retry]').disabled = busy;
      this.status.textContent = message;
    }

    clearError() { this.error.hidden = true; }

    showError(error) {
      this.querySelector('[data-cart-error-message]').textContent = error?.shopifyMessage || this.strings.error;
      this.error.hidden = false;
    }

    async request(url, body) {
      const response = await fetch(url, {
        method: body ? 'POST' : 'GET', credentials: 'same-origin', cache: 'no-store',
        headers: body instanceof FormData ? { Accept: 'application/json' } : { Accept: 'application/json', ...(body ? { 'Content-Type': 'application/json' } : {}) },
        body: body instanceof FormData ? body : body ? JSON.stringify(body) : undefined,
        signal: AbortSignal.timeout(20000)
      });
      const data = await response.json();
      if (!response.ok || data.status >= 400) {
        const error = new Error('Cart request failed');
        if (typeof data.description === 'string') error.shopifyMessage = data.description;
        throw error;
      }
      return data;
    }

    async read() {
      // Cart and compare-at prices load in parallel, then render once, so the drawer fills in one step.
      const [cart, html] = await Promise.all([this.request(`${this.dataset.cartUrl}.js`), this.fetchPriceSection()]);
      if (!Array.isArray(cart.items) || typeof cart.total_price !== 'number') throw new Error('Invalid cart');
      this.render(cart, this.parsePrices(html, cart));
      this.stale = false;
    }

    async fetchPriceSection() {
      if (!this.dataset.priceSection) return null;
      try {
        const url = new URL(this.dataset.cartUrl, window.location.origin);
        url.searchParams.set('section_id', this.dataset.priceSection);
        const response = await fetch(url.href, { credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'text/html' }, signal: AbortSignal.timeout(5000) });
        return response.ok ? await response.text() : null;
      } catch { return null; }
    }

    parsePrices(html, cart) {
      if (typeof html !== 'string' || !cart.items.length) return new Map();
      try {
        const source = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-cart-price-data]');
        return this.validatePrices(JSON.parse(source?.textContent || 'null'), cart);
      } catch { return new Map(); }
    }

    showSkeleton() {
      if (this.cart?.items?.length) return;
      this.querySelector('[data-cart-empty]').hidden = true;
      const row = document.createElement('div');
      row.className = 'gn-cart-drawer__skeleton';
      row.setAttribute('aria-hidden', 'true');
      row.innerHTML = '<span></span><span><i></i><i></i><i></i></span>';
      this.items.replaceChildren(row);
    }

    validatePrices(data, cart) {
      const prices = new Map();
      if (!data || data.currency !== cart.currency || data.total_price !== cart.total_price || !Array.isArray(data.items) || data.items.length !== cart.items.length) return prices;
      const rows = new Map(data.items.map(item => [item.key, item]));
      if (rows.size !== cart.items.length) return prices;
      const matches = cart.items.every(item => {
        const row = rows.get(item.key);
        return row && row.variant_id === item.variant_id && row.quantity === item.quantity && row.original_line_price === item.original_line_price && row.final_line_price === item.final_line_price;
      });
      if (!matches) return prices;
      cart.items.forEach(item => {
        const row = rows.get(item.key);
        if (Number.isSafeInteger(row.compare_at_price) && Number.isSafeInteger(row.variant_price) && row.variant_price >= 0 && row.compare_at_price > row.variant_price && Number.isSafeInteger(row.compare_at_price * item.quantity)) prices.set(item.key, row.compare_at_price);
      });
      return prices;
    }

    async loadPrices(cart) {
      if (!cart.items.length || !this.dataset.priceSection) return new Map();
      try {
        let html = cart.sections?.[this.dataset.priceSection];
        if (typeof html !== 'string') {
          const url = new URL(this.dataset.cartUrl, window.location.origin);
          url.searchParams.set('section_id', this.dataset.priceSection);
          const response = await fetch(url.href, { credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'text/html' }, signal: AbortSignal.timeout(5000) });
          if (!response.ok) return new Map();
          html = await response.text();
        }
        const document = new DOMParser().parseFromString(html, 'text/html');
        const source = document.querySelector('[data-cart-price-data]');
        return this.validatePrices(JSON.parse(source?.textContent || 'null'), cart);
      } catch { return new Map(); }
    }

    linePrices(item) {
      const final = item.final_line_price;
      const original = Math.max(final, item.original_line_price, (this.priceData?.get(item.key) || 0) * item.quantity);
      return { original, final, savings: original - final };
    }

    cartPrices(cart) {
      const original = Math.max(cart.total_price, cart.items.reduce((total, item) => total + this.linePrices(item).original, 0));
      return { original, final: cart.total_price, savings: original - cart.total_price };
    }

    async refresh() {
      if (this.busy) return;
      this.clearError();
      this.setBusy(true, this.strings.loading);
      try { await this.read(); }
      catch (error) { this.stale = true; this.showError(error); }
      finally { this.setBusy(false); }
    }

    async add(form, opener) {
      if (this.busy) {
        const error = new Error('Cart busy');
        error.shopifyMessage = this.strings.busy;
        throw error;
      }
      const data = new FormData(form);
      this.showSkeleton();
      this.open(opener);
      this.clearError();
      this.setBusy(true, this.strings.adding);
      let added = false;
      let failure;
      try {
        await this.request(this.dataset.addUrl, data);
        added = true;
        await this.read();
      } catch (error) {
        failure = error;
        try { await this.read(); } catch { this.stale = true; }
        this.showError(error);
      } finally {
        this.setBusy(false, added && !this.stale ? this.strings.added : '');
      }
      if (failure && !added) throw failure;
    }

    async change(key, quantity, focusTarget) {
      if (this.busy) return;
      const previousItem = this.cart?.items.find(item => item.key === key);
      const identity = item => JSON.stringify([item.variant_id, item.selling_plan_allocation?.selling_plan?.id || null, Object.entries(item.properties || {}).sort(([a], [b]) => a.localeCompare(b))]);
      this.clearError();
      this.setBusy(true, this.strings.loading);
      try {
        const body = { id: key, quantity };
        if (this.dataset.priceSection) { body.sections = this.dataset.priceSection; body.sections_url = this.dataset.cartUrl; }
        const cart = await this.request(this.dataset.changeUrl, body);
        this.render(cart, await this.loadPrices(cart));
        this.stale = false;
        this.status.textContent = this.strings.updated;
      } catch (error) {
        try { await this.read(); } catch { this.stale = true; }
        this.showError(error);
      } finally {
        this.setBusy(false, this.status.textContent === this.strings.updated ? this.strings.updated : '');
        if (this.dialog.open) {
          let focusKey = key;
          if (quantity > 0 && previousItem && !this.cart?.items.some(item => item.key === key)) {
            const matches = this.cart?.items.filter(item => identity(item) === identity(previousItem)) || [];
            if (matches.length === 1) focusKey = matches[0].key;
          }
          const row = [...this.items.children].find(item => item.dataset.lineKey === focusKey);
          const selector = focusTarget === 'input' ? '[data-cart-quantity]' : focusTarget === 'remove' ? '[data-cart-remove]' : `[data-cart-step="${focusTarget}"]`;
          (row?.querySelector(selector) || this.querySelector('[data-cart-close]')).focus({ preventScroll: true });
        }
      }
    }

    money(cents) {
      const currency = this.cart?.currency || this.dataset.currency;
      return new Intl.NumberFormat(this.dataset.locale, { style: 'currency', currency, currencyDisplay: 'symbol' }).format(cents / 100);
    }

    url(value) {
      try {
        const url = new URL(value, window.location.origin);
        if (['https:', 'http:'].includes(url.protocol)) return url.href;
      } catch {}
      return '';
    }

    render(cart, priceData = this.priceData) {
      this.cart = cart;
      this.priceData = priceData || new Map();
      const fragment = document.createDocumentFragment();
      cart.items.forEach(item => {
        const row = this.querySelector('[data-cart-item-template]').content.firstElementChild.cloneNode(true);
        row.dataset.lineKey = item.key;
        const text = (selector, value) => { const element = row.querySelector(selector); element.textContent = value || ''; element.hidden = !value; };
        row.querySelectorAll('[data-item-link]').forEach(link => { link.href = this.url(item.url); });
        text('[data-item-title]', item.product_title || item.title);
        text('[data-item-variant]', item.product_has_only_default_variant || item.variant_title === 'Default Title' ? '' : item.variant_title);
        text('[data-item-plan]', item.selling_plan_allocation?.selling_plan?.name);
        const price = this.linePrices(item);
        text('[data-item-price]', this.money(price.final));
        text('[data-item-original]', price.savings > 0 ? this.money(price.original) : '');
        text('[data-item-savings]', price.savings > 0 ? this.strings.save.replace('__AMOUNT__', this.money(price.savings)) : '');
        const image = row.querySelector('[data-item-image]');
        const imageUrl = this.url(item.featured_image?.url || item.image);
        if (imageUrl && (item.featured_image?.url || item.image)) { image.src = imageUrl; image.alt = item.product_title || item.title; } else image.hidden = true;
        Object.entries(item.properties || {}).forEach(([key, value]) => {
          if (!value || key.startsWith('_')) return;
          const line = document.createElement('p');
          line.textContent = `${key}: ${value}`;
          row.querySelector('[data-item-properties]').append(line);
        });
        (item.line_level_discount_allocations || []).forEach(discount => {
          const line = document.createElement('p');
          line.textContent = `${discount.discount_application.title} (−${this.money(discount.amount)})`;
          row.querySelector('[data-item-discounts]').append(line);
        });
        const input = row.querySelector('[data-cart-quantity]');
        input.value = item.quantity;
        input.min = item.quantity_rule?.min || 1;
        input.step = item.quantity_rule?.increment || 1;
        if (item.quantity_rule?.max) input.max = item.quantity_rule.max;
        input.setAttribute('aria-label', `${this.strings.quantity}: ${item.product_title || item.title}`);
        row.querySelector('[data-cart-remove]').setAttribute('aria-label', `${this.strings.remove}: ${item.product_title || item.title}`);
        fragment.append(row);
      });
      this.items.replaceChildren(fragment);
      this.querySelector('[data-cart-empty]').hidden = cart.item_count !== 0;
      this.checkout.hidden = cart.item_count === 0;
      const price = this.cartPrices(cart);
      this.querySelector('[data-cart-total]').textContent = this.money(price.final);
      this.querySelector('[data-cart-original]').textContent = this.money(price.original);
      this.querySelector('[data-cart-original]').hidden = price.savings <= 0;
      this.querySelector('[data-cart-savings]').textContent = this.money(price.savings);
      this.querySelector('[data-cart-savings-row]').hidden = price.savings <= 0;
      this.querySelector('[data-drawer-count]').textContent = cart.item_count;
      document.querySelectorAll('.gn-cart-count').forEach(count => { count.textContent = cart.item_count; });
      const discounts = this.querySelector('[data-cart-discounts]');
      discounts.replaceChildren();
      (cart.cart_level_discount_applications || []).forEach(discount => {
        const line = document.createElement('p');
        const label = document.createElement('span');
        const amount = document.createElement('span');
        label.textContent = discount.title;
        amount.textContent = `−${this.money(discount.total_allocated_amount)}`;
        line.append(label, amount);
        discounts.append(line);
      });
      document.dispatchEvent(new CustomEvent('gn:cart-updated', { detail: { cart } }));
    }
  });
}
